import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Button, Menu, MenuItem } from '@mui/material';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { PostListItem } from 'api';
import { ReactComponent as PinnedIcon } from 'assets/icons/postPinned.svg';
import { Avatar } from 'components/Avatar';
import { NewConfirmDialog } from 'components/Dialogs';
import { usePostDetailsPageWithoutApiCall } from 'components/PostTopBar/usePostDetailsPageWithoutApiCall';
import { formatDateTime } from 'features/notifications/components/Notification/utils';
import { useAuth, useWindowSize } from 'hooks';
import { Links } from 'settings';
import { theme } from 'styles';

import {
  AuthorDetailsW,
  AuthorName,
  AuthorNameW,
  PostHeaderRightW,
  PublishedTime,
  TopBarW,
} from './styled';

export function PostTopBar({
  id,
  author,
  postTitle,
  authorName,
  publishedAt,
  isPoll = false,
  isAutomatedPost,
  pin = false,
}: {
  id: string;
  author: PostListItem['author'];
  postTitle: string;
  publishedAt?: string;
  isPoll?: boolean;
  authorName: string;
  isAutomatedPost?: boolean;
  pin?: boolean;
}): ReactElement {
  const { isMobile } = useWindowSize();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isManager, user } = useAuth();
  const isAuthor = user?.id === author.id;

  const editLink = Links.posts.edit.postId;

  const onNavigateToEditDocument = () => {
    navigate(
      generatePath(editLink, {
        documentId: id,
      }),
    );
  };

  function goToPublisherDetailsPage(id: string) {
    if (!isAutomatedPost) {
      navigate(
        generatePath(Links.we.employees.employee.index, {
          employeeId: id,
        }),
      );
    }
  }

  const removeDialogTitle = isPoll
    ? t('poll.removeModal.title')
    : t('post.details.removeModal.title', { postTitle });
  const removeDialogSubTitle = isPoll
    ? t('poll.removeModal.subtitle')
    : t('post.details.removeModal.subtitle');
  const successButtonText = isPoll
    ? t('poll.removeModal.successButton')
    : t('post.details.removeModal.successButton');
  const cancelButtonText = isPoll
    ? t('poll.removeModal.cancelButton')
    : t('post.details.removeModal.cancelButton');

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const { isOpen, handleOpenModal, handleCloseModal, handleDeletePost } =
    usePostDetailsPageWithoutApiCall(id || '');

  const handleDelete = () => {
    setAnchorEl(null);
    handleOpenModal();
  };

  return (
    <TopBarW>
      <AuthorDetailsW
        isAutomatedPost={isAutomatedPost}
        onClick={() => goToPublisherDetailsPage(author.id)}
      >
        <Avatar
          size={isMobile ? 's' : '44'}
          userId={author.id}
          filename={author.avatar?.medium || ''}
          isAutomatedPost={isAutomatedPost}
        />
        <AuthorNameW>
          <AuthorName>{authorName}</AuthorName>
          <PublishedTime>{publishedAt && formatDateTime(new Date(publishedAt))}</PublishedTime>
        </AuthorNameW>
      </AuthorDetailsW>
      <PostHeaderRightW>
        {pin && <PinnedIcon style={{ display: 'flex', alignSelf: 'center' }} />}

        {(isManager || isAuthor) && (
          <>
            <Button
              onClick={handleClick}
              endIcon={<MoreVertIcon style={{ fontSize: '24px', fontWeight: 700 }} />}
              style={{
                padding: '0px',
                width: '44px',
                height: '44px',
                minWidth: '0',
              }}
              sx={{ '& .MuiButton-endIcon': { margin: 0 } }}
            />
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={() => setAnchorEl(null)}
              PaperProps={{
                sx: {
                  width: 200,
                  padding: '8px',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                },
              }}
              MenuListProps={{ disablePadding: true }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {!isAutomatedPost && (
                <MenuItem
                  onClick={onNavigateToEditDocument}
                  sx={{ height: '44px', '&:hover': { backgroundColor: theme.palette.info.light } }}
                >
                  {isPoll ? t('document.list.editPoll') : t('document.list.editPost')}
                </MenuItem>
              )}
              <MenuItem
                onClick={handleDelete}
                sx={{ height: '44px', '&:hover': { backgroundColor: theme.palette.info.light } }}
              >
                {isPoll ? t('document.list.deletePoll') : t('document.list.deletePost')}
              </MenuItem>
            </Menu>
          </>
        )}
      </PostHeaderRightW>
      {isOpen && (
        <NewConfirmDialog
          title={removeDialogTitle}
          subTitle={removeDialogSubTitle}
          open={isOpen}
          onConfirm={handleDeletePost}
          onClose={handleCloseModal}
          submitLabel={successButtonText}
          cancelLabel={cancelButtonText}
        />
      )}
    </TopBarW>
  );
}
